/**
 * CalendarHeader - Navigation and view controls
 */

import { useState } from 'react';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useCalendarStore, type CalendarView } from '../../stores/calendar-store';
import { Button } from '../ui';
import { Select } from '../ui';
import { GenerateRoutesModal } from './GenerateRoutesModal';
import clsx from 'clsx';

const ChevronLeftIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const viewOptions: { value: CalendarView; label: string }[] = [
  { value: 'month', label: 'Maand' },
  { value: 'week', label: 'Week' },
  { value: 'day', label: 'Dag' },
];

export function CalendarHeader() {
  const {
    view,
    currentDate,
    chauffeurs,
    selectedChauffeurId,
    lastUpdated,
    setView,
    setSelectedChauffeur,
    goToNext,
    goToPrevious,
    goToToday,
    refresh,
  } = useCalendarStore();

  const [showGenerateModal, setShowGenerateModal] = useState(false);

  const getTitle = () => {
    switch (view) {
      case 'month':
        return format(currentDate, 'MMMM yyyy', { locale: nl });
      case 'week': {
        const van = startOfWeek(currentDate, { locale: nl });
        const tot = endOfWeek(currentDate, { locale: nl });
        const zelfdeMaand = van.getMonth() === tot.getMonth();
        const bereik = zelfdeMaand
          ? `${format(van, 'd', { locale: nl })} t/m ${format(tot, 'd MMMM yyyy', { locale: nl })}`
          : `${format(van, 'd MMM', { locale: nl })} t/m ${format(tot, 'd MMM yyyy', { locale: nl })}`;
        return `Week ${format(currentDate, 'w', { locale: nl })} · ${bereik}`;
      }
      case 'day':
        return format(currentDate, 'EEEE d MMMM yyyy', { locale: nl });
    }
  };

  const chauffeurOptions = [
    { value: '', label: 'Alle chauffeurs' },
    ...chauffeurs.map(c => ({ value: c.id, label: c.name })),
  ];

  return (
    <div className="flex items-center justify-between py-4 px-6 bg-white border-b border-gray-200">
      {/* Left: Navigation */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1">
          <button
            onClick={goToPrevious}
            className="p-2 hover:bg-gray-100 rounded-sm transition-colors"
            aria-label="Vorige"
          >
            <ChevronLeftIcon />
          </button>
          <button
            onClick={goToNext}
            className="p-2 hover:bg-gray-100 rounded-sm transition-colors"
            aria-label="Volgende"
          >
            <ChevronRightIcon />
          </button>
        </div>

        <Button variant="secondary" size="sm" onClick={goToToday}>
          Vandaag
        </Button>

        <h2 className="text-xl font-heading font-semibold text-main capitalize">
          {getTitle()}
        </h2>
      </div>

      {/* Right: Filters and view toggle */}
      <div className="flex items-center gap-4">
        {/* Legenda duurkleuren (levende route) */}
        <span className="hidden xl:inline-flex items-center gap-2 text-[11px] text-muted" title="Kleur van de balken op de routekaart: ophalen, afleveren en dagduur tegen hun grens">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" />ruimte</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />krap</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />vol of over</span>
        </span>

        {/* Last updated indicator */}
        {lastUpdated && (
          <span className="text-xs text-muted">
            Bijgewerkt: {format(lastUpdated, 'HH:mm:ss')}
          </span>
        )}

        {/* Generate routes button */}
        <Button variant="secondary" size="sm" onClick={() => setShowGenerateModal(true)}>
          Genereer routes
        </Button>

        {/* Refresh button */}
        <button
          onClick={refresh}
          className="p-2 hover:bg-gray-100 rounded-sm transition-colors"
          title="Vernieuwen"
        >
          <svg className="w-5 h-5 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>

        {/* Chauffeur filter */}
        <div className="w-48">
          <Select
            options={chauffeurOptions}
            value={selectedChauffeurId || ''}
            onChange={(e) => setSelectedChauffeur(e.target.value || null)}
            placeholder="Filter chauffeur"
          />
        </div>

        {/* View toggle */}
        <div className="flex bg-gray-100 rounded-md p-1">
          {viewOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setView(option.value)}
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-sm transition-all duration-150',
                view === option.value
                  ? 'bg-white text-main shadow-sm'
                  : 'text-muted hover:text-main'
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <GenerateRoutesModal
        open={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
      />
    </div>
  );
}
